import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import EstimateResult from "@/components/planner/EstimateResult";
import { formatApiError } from "@/lib/api";
import { trackAbandon, trackComplete } from "@/lib/abandon";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n, c = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n || 0);

export default function QuoteAccept() {
  const { token } = useParams();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);

  const load = useCallback(() => {
    axios.get(`${BASE}/quote/${token}`).then((res) => {
      setQ(res.data);
      if (res.data.deposit_paid) { setPaid(true); trackComplete("quote_view", { ref_id: token }); }
      else trackAbandon("quote_view", { ref_id: token });
    }).catch(() => setInvalid(true));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe return (?paid=1&session_id=...) — poll status.
  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const res = await axios.get(`${BASE}/quote/status/${sid}`);
        if (res.data.payment_status === "paid") { setPaid(true); load(); return; }
        if (res.data.status === "expired" || tries > 8) { toast.error("Payment not completed."); return; }
      } catch (e) { /* keep trying */ }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const payDeposit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${BASE}/quote/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setBusy(false);
    }
  };

  if (invalid)
    return <Centered><h1 className="font-serif text-2xl text-charcoal-800">This quote link is invalid or expired.</h1>
      <p className="text-charcoal-500 mt-2">Please contact Hola Movers for a new link.</p></Centered>;
  if (!q) return <Centered><Loader2 className="animate-spin text-terracotta" /></Centered>;

  return (
    <div className="min-h-screen bg-[#F7F4EF]">
      <header className="bg-white border-b border-sand-200">
        <div className="max-w-3xl mx-auto flex items-center gap-2 px-5 h-16">
          <img src="/brand/logo-mark.png" alt="Hola Movers" className="h-9 w-auto" data-testid="quote-page-logo" />
          <span className="font-serif text-lg text-charcoal-800">Hola Movers · Your Quote</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        {paid ? (
          <div className="rounded-2xl bg-white border border-sage/30 p-6 mb-6 flex items-start gap-3" data-testid="quote-paid-banner">
            <CheckCircle2 className="text-sage shrink-0" size={28} />
            <div>
              <h1 className="font-serif text-2xl text-charcoal-800">Deposit received — thank you!</h1>
              <p className="text-charcoal-600 mt-1">Your move is secured. A Hola Movers coordinator will be in touch shortly to lock in the details.</p>
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-serif text-3xl text-charcoal-800">
              {q.client_name ? `${q.client_name}, here's your quote` : "Your relocation quote"}
            </h1>
            <p className="text-charcoal-600 mt-2 mb-6">{q.safety_statement}</p>
          </>
        )}

        {q.route && q.pricing && <EstimateResult route={q.route} pricing={q.pricing} />}

        {!paid && q.deposit_amount > 0 && (
          <div className="mt-8 rounded-2xl bg-charcoal-800 text-white p-6" data-testid="quote-accept-cta">
            <div className="flex items-center gap-2 text-sage-200 mb-1">
              <ShieldCheck size={18} /> <span className="text-sm">Secure your move</span>
            </div>
            <h3 className="font-serif text-2xl">Accept this quote with a {money(q.deposit_amount, q.currency)} deposit</h3>
            <p className="text-sand-100/80 text-sm mt-1 mb-4">Your deposit goes toward your move and locks in your coordinator. Refundable per our terms — no surprises.</p>
            <button data-testid="quote-pay-deposit" onClick={payDeposit} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3 text-sm font-medium hover:bg-terracotta-hover disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              Accept &amp; pay {money(q.deposit_amount, q.currency)} deposit
            </button>
            <p className="text-xs text-sand-100/50 mt-3">Secure payment via Stripe. This is an estimate; final route and price are confirmed before booking.</p>
          </div>
        )}

        <div className="mt-6 text-center">
          <a
            data-testid="quote-whatsapp-cta"
            href="https://wa.me/529993616898?text=Hola!%20I%20have%20a%20question%20about%20my%20Hola%20Movers%20relocation%20quote."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-[#25D366] text-[#128C4B] px-6 py-2.5 text-sm font-medium hover:bg-[#25D366]/10 transition-colors"
          >
            Questions? Chat with us on WhatsApp
          </a>
        </div>
      </main>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center text-center px-6">{children}</div>;
}
